/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/OverviewTab" />
import { MarkdownPreviewWithAutocomplete, MarkdownTextFieldWithAutocomplete } from "Mention/MarkdownWithAutocomplete/Components";
import * as React from "react";
import { PullRequestStatus } from "Repos/Common/Contracts/Git";
import { wrapWithAttachmentDropTarget } from "Repos/PullRequest/Attachments/Attachment";
import { getPullRequestDescriptionService, maxDescriptionLength } from "Repos/PullRequestCommon/PullRequestDescriptionService";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { shortcutCommands } from "VSS/Features/MarkdownEditor/Commands/Shortcut";
import { MarkdownToolbar } from "VSS/Features/MarkdownEditor/Components";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { KeyCode } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { getMarkdownPreviewProps, getMarkdownTextFieldProps, getMarkdownToolbarProps } from "../../Markdown/DiscussionProps";
import { hasPermission, PullRequestPermissions } from "../../PullRequestPermissionService";
import { getPullRequestViewService } from "../../PullRequestViewService";
import { ActivityFeed } from "../ActivityFeed/ActivityFeed";
import { CompletionStatus } from "./CompletionStatus";
import { OverviewExtensions } from "./OverviewExtensions";
import { Reviewers } from "./Reviewers";
import { Tags } from "./Tags";

export class OverviewTabContent extends VssComponent {
    public render() {
        return (
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    if (screenSizeProps.screenSize < ScreenSize.medium) {
                        return <SmallLayout pageContext={this.context.pageContext} />;
                    } else {
                        return <MediumLayout pageContext={this.context.pageContext} />;
                    }
                }}
            </ScreenSizeObserver>
        );
    }
}

interface IOverviewLayoutProps {
    pageContext: IVssPageContext;
}

const SmallLayout: React.FunctionComponent<IOverviewLayoutProps> = props => {
    return (
        <div className="flex-column flex-grow rhythm-vertical-16 h-scroll-hidden shadow-padding">
            <CompletionStatus />
            <PullRequestDescription pageContext={props.pageContext} />
            <div className="repos-overview-right-pane-inlined flex-column flex-noshrink rhythm-vertical-16">
                <Reviewers />
                <Tags />
                <OverviewExtensions />
            </div>
            <ActivityFeed />
        </div>
    );
};

const MediumLayout: React.FunctionComponent<IOverviewLayoutProps> = props => {
    return (
        <div className="flex-row">
            <div className="flex-column flex-grow rhythm-vertical-16 h-scroll-hidden shadow-padding">
                <CompletionStatus />
                <PullRequestDescription pageContext={props.pageContext} />
                <ActivityFeed />
            </div>
            <div className="repos-overview-right-pane flex-column flex-noshrink rhythm-vertical-24">
                <Reviewers />
                <Tags />
                <OverviewExtensions />
            </div>
        </div>
    );
};

interface IPullRequestDescriptionProps {
    pageContext: IVssPageContext;
}

const PullRequestDescription = (props: IPullRequestDescriptionProps): JSX.Element => {
    const prViewService = getPullRequestViewService(props.pageContext);
    const prDescriptionService = getPullRequestDescriptionService(props.pageContext);
    const descriptionRef = React.createRef<MarkdownTextFieldWithAutocomplete>();
    const headerRef = React.createRef<Header>();

    return (
        <Observer editMode={prViewService.getDescriptionEditMode()} status={prViewService.getStatus()}>
            {(observerProps: { editMode: boolean; status: PullRequestStatus }) => {
                const canEditDescription =
                    hasPermission(props.pageContext, PullRequestPermissions.UpdateTitleDescription) &&
                    observerProps.status === PullRequestStatus.Active;

                const focusEditDescriptionButton = () => {
                    // Wait for the button to be displayed before focusing
                    requestAnimationFrame(() => {
                        headerRef.current?.focus({ commandBarItemId: "edit-description" });
                    });
                };

                let cardContent: JSX.Element;
                if (observerProps.editMode) {
                    const markdownError = new ObservableValue<Error | null>(null);
                    const pullRequestMarkdownContext = {
                        projectId: prViewService.getTargetRepository().project.id,
                        pullRequestId: prViewService.getId(),
                        repositoryId: prViewService.getTargetRepository().id
                    };

                    // Can be removed when FF will be removed.
                    let isShowWarning: ((value: string) => boolean) | undefined =
                        (value: string) => prDescriptionService.isDescriptionActualLengthValid(value);
                    let localWarningText: string | undefined = Resources.DescriptionTooLongWarning;

                    if (!isFeatureFlagEnabled(props.pageContext, "SourceControl.GitPullRequests.DescriptionLimit", false)) {
                        isShowWarning = undefined;
                        localWarningText = undefined;
                    }

                    const textField = (
                        <MarkdownTextFieldWithAutocomplete
                            {...getMarkdownTextFieldProps(props.pageContext, pullRequestMarkdownContext)}
                            autoFocus={true}
                            error={markdownError}
                            maxLength={maxDescriptionLength}
                            onChange={description => prViewService.setDescription(description)}
                            onError={error => (markdownError.value = error)}
                            onKeyDown={e => {
                                if (e.which === KeyCode.enter && e.ctrlKey) {
                                    prViewService.saveDescription();
                                    focusEditDescriptionButton();

                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                            ref={descriptionRef}
                            shortcutCommands={shortcutCommands}
                            value={prViewService.getDescription()}
                            showInfoLinkForWorkItemLinking
                            warningText={localWarningText}
                            isShowWarning={isShowWarning}
                            showCharacterCounter={isFeatureFlagEnabled(props.pageContext, "SourceControl.GitPullRequests.DescriptionLimit", false)}
                        />
                    );

                    cardContent = (
                        <>
                            {wrapWithAttachmentDropTarget(props.pageContext, pullRequestMarkdownContext, textField, descriptionRef)}
                            <div className="flex-row rhythm-horizontal-8 flex-wrap justify-end">
                                <MarkdownToolbar
                                    {...getMarkdownToolbarProps(props.pageContext, pullRequestMarkdownContext)}
                                    getTextSource={() => descriptionRef.current}
                                />
                                <Button
                                    onClick={() => {
                                        prViewService.setDescriptionEditMode(false);
                                        focusEditDescriptionButton();
                                    }}
                                    text={Resources.Cancel}
                                />
                                <Button
                                    onClick={() => {
                                        prViewService.saveDescription();
                                        focusEditDescriptionButton();
                                    }}
                                    primary={true}
                                    text={Resources.Save}
                                />
                            </div>
                            <MarkdownPreviewWithAutocomplete
                                {...getMarkdownPreviewProps(
                                    props.pageContext,
                                    undefined,
                                    description => prViewService.setDescription(description),
                                    () => canEditDescription
                                )}
                                refreshThrottleMs={1000}
                                value={prViewService.getDescription()}
                            />
                        </>
                    );
                } else {
                    cardContent = (
                        <MarkdownPreviewWithAutocomplete
                            {...getMarkdownPreviewProps(
                                props.pageContext,
                                undefined,
                                description => {
                                    prViewService.setDescription(description);
                                    prViewService.saveDescription();
                                },
                                () => canEditDescription
                            )}
                            value={prViewService.getDescription()}
                        />
                    );
                }

                const cardHeaderButtons: IHeaderCommandBarItem[] = [];
                if (canEditDescription && !observerProps.editMode) {
                    cardHeaderButtons.push({
                        ariaLabel: Resources.EditDescription,
                        iconProps: { iconName: "Edit" },
                        id: "edit-description",
                        onActivate: () => prViewService.setDescriptionEditMode(true),
                        subtle: true,
                        tooltipProps: { text: Resources.EditDescription }
                    });
                }
                return (
                    <Card
                        className="repos-pr-description-card bolt-card-with-header"
                        renderHeader={() => (
                            <Header className="bolt-card-header" commandBarItems={cardHeaderButtons} ref={headerRef} title={Resources.Description} />
                        )}
                    >
                        <div className="repos-pr-description flex-column flex-grow rhythm-vertical-8 padding-4 scroll-hidden">{cardContent}</div>
                    </Card>
                );
            }}
        </Observer>
    );
};
